/**
 * NAO — instrumentación del píxel de Meta sobre el checkout personalizado.
 *
 * Contexto (verificado 2026-08-17 contra el dataset del píxel y el backend):
 *  - El canal "Facebook & Instagram" de Shopify YA envía PageView y ViewContent
 *    (browser + server con dedup propio) en todo el sitio, y AddPaymentInfo/
 *    Purchase para el flujo anticipado (checkout de factura de Shopify → Bold).
 *    Por eso PageView/ViewContent quedan APAGADOS aquí: dispararlos también
 *    desde el tema duplicaría esos eventos. Si ese canal se desconecta algún
 *    día, poner TRACK_PAGEVIEW / TRACK_VIEWCONTENT en true.
 *  - El backend de checkout (nao-checkout-v2.vercel.app) NO envía eventos por
 *    Conversions API: todo lo de este archivo es solo píxel del navegador.
 *
 * Qué agrega este módulo (los tramos del embudo que hoy son invisibles):
 *  - AddToCart          clic en "PEDIR AHORA" (apertura del modal con intención)
 *  - InitiateCheckout   primer foco en un campo del formulario de datos
 *  - AddPaymentInfo     primera interacción con el selector de método de pago
 *  - Lead               teléfono/WhatsApp/email diligenciado (antes de comprar)
 *  - Purchase           SOLO contraentrega (la orden queda creada al confirmar);
 *                       el Purchase de pago anticipado lo reporta el canal de
 *                       Shopify cuando el pago en Bold se completa de verdad.
 *
 * Reglas de este módulo:
 *  - Cero dependencias, cero cambios de UI, y NUNCA puede romper el checkout:
 *    todo handler va envuelto en try/catch y no toca el DOM del flujo.
 *  - No importa nada del importmap @theme/* a propósito: si este asset faltara,
 *    el checkout sigue funcionando igual.
 *  - Dedup: eventID único por evento + banderas en sessionStorage para no
 *    repetir eventos en recargas o reaperturas del modal.
 *  - Advanced Matching: cuando el usuario ya escribió email/teléfono/nombre se
 *    re-inicializa el píxel con esos datos (el píxel los hashea localmente).
 */

const PIXEL_ID = '930420428542561';
const CURRENCY = 'COP';

// Apagados: los cubre el canal Facebook & Instagram de Shopify (ver cabecera).
const TRACK_PAGEVIEW = false;
const TRACK_VIEWCONTENT = false;

const FLAGS_KEY = 'nao_fbq_v1';

// ---------------------------------------------------------------------------
// Utilidades defensivas
// ---------------------------------------------------------------------------

function safe(fn) {
    return function () {
        try {
            return fn.apply(this, arguments);
        } catch (error) {
            // El tracking jamás debe interrumpir el flujo de compra.
        }
    };
}

function readFlags() {
    try {
        return JSON.parse(sessionStorage.getItem(FLAGS_KEY)) || {};
    } catch (error) {
        return {};
    }
}

function markFlag(name) {
    try {
        const flags = readFlags();
        flags[name] = 1;
        sessionStorage.setItem(FLAGS_KEY, JSON.stringify(flags));
    } catch (error) {
        // Sin sessionStorage (p. ej. navegación privada) se tolera el re-disparo.
    }
}

function hasFlag(name) {
    return readFlags()[name] === 1;
}

function eventId(name) {
    let rand = '';
    try {
        rand = crypto.randomUUID();
    } catch (error) {
        rand = Date.now() + '_' + Math.random().toString(36).slice(2);
    }
    return 'nao_' + name.toLowerCase() + '_' + rand;
}

// ---------------------------------------------------------------------------
// Bootstrap del píxel (snippet oficial de Meta, sin PageView automático)
// ---------------------------------------------------------------------------

function ensurePixel() {

    if (window.__naoFbqReady) return true;

    if (!window.fbq) {
        const n = (window.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        });
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(t, s);
    }

    window.fbq('init', PIXEL_ID);

    if (TRACK_PAGEVIEW) {
        window.fbq('track', 'PageView', {}, { eventID: eventId('PageView') });
    }

    window.__naoFbqReady = true;
    return true;
}

function track(name, params, id) {
    ensurePixel();
    window.fbq('track', name, params || {}, { eventID: id || eventId(name) });
}

// ---------------------------------------------------------------------------
// Datos del producto y del formulario (leídos del DOM existente, sin tocarlo)
// ---------------------------------------------------------------------------

function findWrapper(node) {
    return (
        (node && node.closest && node.closest('.nao-checkout-wrapper')) ||
        document.querySelector('.nao-checkout-wrapper')
    );
}

function productData(wrapper) {

    const data = {
        variantId: '',
        price: 0,
        name: ''
    };

    if (!wrapper) return data;

    const variantInput = wrapper.querySelector('.js-nao-variant-id');
    if (variantInput) data.variantId = variantInput.value || '';

    const priceEl = wrapper.querySelector('.js-nao-base-price');
    if (priceEl && priceEl.dataset.price) {
        data.price = parseInt(priceEl.dataset.price, 10) / 100 || 0;
    }

    const titleEl = wrapper.querySelector('.nao-prod-title');
    if (titleEl) data.name = titleEl.textContent.trim();

    // Variante elegida (aroma/color): el título de la variante ya está en el DOM.
    const variantTitleEl = wrapper.querySelector('.js-nao-variant-title');
    const variantTitle = variantTitleEl ? variantTitleEl.textContent.trim() : '';
    if (variantTitle && variantTitle !== 'Default Title') {
        data.name = data.name ? data.name + ' - ' + variantTitle : variantTitle;
    }

    return data;
}

// Lineas del selector de aromas, ya en el formato `contents` que
// espera Meta. Devuelve null cuando el producto no usa aromas, para que el
// resto del modulo siga por el camino de una sola variante.
function aromaContents(wrapper) {

    const rows = wrapper ? wrapper.querySelectorAll('.js-nao-aroma') : [];

    if (!rows.length) return null;

    const contents = [];

    rows.forEach(function (row) {

        const input = row.querySelector('.js-nao-aroma-qty');
        const quantity = input ? parseInt(input.value, 10) || 0 : 0;

        if (quantity <= 0) return;

        contents.push({
            id: row.dataset.variantId || '',
            quantity: quantity,
            item_price: (parseInt(row.dataset.price, 10) || 0) / 100
        });
    });

    return contents;
}

// Unidades elegidas en el formulario. Con selector de aromas es la suma de las
// lineas; con selector de cantidad, ese numero; sin ninguno de los dos (Kit de
// Inicio) el checkout es de una unidad, que es como se venia reportando.
function currentQuantity(wrapper) {

    const contents = aromaContents(wrapper);

    if (contents) {
        return contents.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    }

    const input = wrapper && wrapper.querySelector('.js-nao-qty');
    if (!input) return 1;
    const parsed = parseInt(input.value, 10);
    return parsed && parsed > 0 ? parsed : 1;
}

function baseParams(wrapper, extraValue) {

    const product = productData(wrapper);
    const aromas = aromaContents(wrapper);

    // Con aromas el valor se suma linea a linea: cada una puede tener su propio
    // precio, y reportar precio_unico x cantidad falsearia el ROAS.
    const contents = aromas || (
        product.variantId
            ? [{ id: product.variantId, quantity: currentQuantity(wrapper), item_price: product.price }]
            : []
    );

    const subtotal = contents.reduce(
        function (sum, item) { return sum + item.item_price * item.quantity; },
        0
    );

    return {
        value: subtotal + (extraValue || 0),
        currency: CURRENCY,
        content_ids: contents.map(function (item) { return item.id; }),
        content_name: product.name,
        content_type: 'product',
        contents: contents
    };
}

function currentShippingCost(wrapper) {
    const input = wrapper && wrapper.querySelector('.js-nao-shipping-cost');
    return input ? parseInt(input.value, 10) || 0 : 0;
}

// Advanced Matching: se pasan los valores en claro y fbevents.js los hashea
// (SHA-256) antes de enviarlos. Normalización según la doc de Meta.
function advancedMatching(wrapper) {

    if (!wrapper) return null;

    const form = wrapper.querySelector('.js-nao-form');
    if (!form) return null;

    const val = (name) => {
        const field = form.querySelector('[name="' + name + '"]');
        return field && field.value ? field.value.trim() : '';
    };

    const normPhone = (raw) => {
        let digits = (raw || '').replace(/\D/g, '');
        if (digits.length === 10 && digits.charAt(0) === '3') {
            digits = '57' + digits; // celular colombiano sin indicativo
        }
        return digits;
    };

    const am = {};
    const email = val('email');
    const phone = normPhone(val('phone') || val('whatsapp_phone'));

    if (email) am.em = email.toLowerCase();
    if (phone) am.ph = phone;
    // El formulario tiene un solo campo de nombre; Meta quiere fn y ln por
    // separado, así que se parte por el primer espacio igual que el backend.
    const nombre = val('full_name');

    if (nombre) {
        const partes = nombre.split(/\s+/);
        am.fn = partes[0].toLowerCase();
        if (partes.length > 1) am.ln = partes.slice(1).join(' ').toLowerCase();
    }
    if (val('city')) am.ct = val('city').toLowerCase().replace(/[^a-záéíóúñü]/g, '');
    if (val('province')) am.st = val('province').toLowerCase().replace(/[^a-záéíóúñü]/g, '');
    if (email || phone) am.country = 'co';

    return email || phone ? am : null;
}

function applyAdvancedMatching(wrapper) {
    const am = advancedMatching(wrapper);
    if (am) {
        ensurePixel();
        // Re-inicializar el mismo píxel con userData actualiza el Advanced
        // Matching de los eventos siguientes (patrón documentado por Meta).
        window.fbq('init', PIXEL_ID, am);
    }
}

// ---------------------------------------------------------------------------
// Eventos del embudo (delegación en document: no toca los listeners del checkout)
// ---------------------------------------------------------------------------

function fireOnce(flag, name, params, id) {
    if (hasFlag(flag)) return;
    markFlag(flag);
    track(name, params, id);
}

function methodLabel(target) {
    if (target.classList.contains('js-nao-loss-switch')) return 'Anticipado';
    if (target.classList.contains('js-nao-loss-continue')) return 'Contra Entrega';
    const cost = parseInt(target.dataset.value, 10) || 0;
    return cost > 0 ? 'Contra Entrega' : 'Anticipado';
}

function initTracking() {

    if (window.__naoMetaEventsInit) return;
    window.__naoMetaEventsInit = true;

    // No inicializar el píxel hasta que exista el checkout en la página.
    if (document.querySelector('.nao-checkout-wrapper')) {
        ensurePixel();

        if (TRACK_VIEWCONTENT && !hasFlag('vc_' + location.pathname)) {
            markFlag('vc_' + location.pathname);
            track('ViewContent', baseParams(findWrapper(null), 0));
        }
    }

    // AddToCart — intencion real de compra.
    //
    // Con modal esa intencion es el clic en "PEDIR AHORA". Embebido NO hay ese
    // boton, asi que la senal equivalente es la primera interaccion dentro del
    // checkout. Sin esto el flujo inline se quedaria sin AddToCart y el embudo
    // de Meta perderia un paso.
    function ensureAddToCart(wrapper) {
        fireOnce('AddToCart', 'AddToCart', baseParams(wrapper, 0));
    }

    document.addEventListener('click', safe(function (event) {
        const cta = event.target.closest && event.target.closest('.js-nao-cta');
        if (!cta) return;
        ensureAddToCart(findWrapper(cta));
    }));

    const onInlineIntent = safe(function (event) {
        const wrapper = event.target.closest && event.target.closest('.nao-checkout-wrapper');
        // Solo el flujo embebido: donde hay CTA, la senal es el clic en el CTA.
        if (!wrapper || wrapper.querySelector('.js-nao-cta')) return;
        ensureAddToCart(wrapper);
    });

    document.addEventListener('click', onInlineIntent);
    document.addEventListener('focusin', onInlineIntent);

    // "Realizar pedido" del flujo embebido. El clic es intencion explicita, asi
    // que cierra AddToCart y abre InitiateCheckout sin esperar al foco: el
    // primer campo lo dispararia igual, pero solo si el navegador considera el
    // documento enfocado (ver la trampa del headless en CLAUDE.md 19.5).
    document.addEventListener('click', safe(function (event) {

        const opener = event.target.closest && event.target.closest('.js-nao-open-form');

        if (!opener) return;

        const wrapper = findWrapper(opener);

        ensureAddToCart(wrapper);

        fireOnce(
            'InitiateCheckout',
            'InitiateCheckout',
            baseParams(wrapper, currentShippingCost(wrapper))
        );
    }));

    // InitiateCheckout — primer foco en un campo del formulario de datos/envío.
    document.addEventListener('focusin', safe(function (event) {
        const field = event.target.closest && event.target.closest('.js-nao-form input, .js-nao-form select');
        if (!field) return;
        const wrapper = findWrapper(field);
        // Garantiza el orden del embudo: AddToCart nunca llega despues de IC.
        ensureAddToCart(wrapper);
        fireOnce('InitiateCheckout', 'InitiateCheckout', baseParams(wrapper, currentShippingCost(wrapper)));
    }));

    // AddPaymentInfo — primera interacción con el selector de método de pago
    // (tarjetas del radiogroup, o botones del modal de aversión a la pérdida).
    const PAYMENT_SELECTOR = '.js-nao-method, .js-nao-loss-switch, .js-nao-loss-continue';

    const onPaymentInteraction = safe(function (event) {
        const target = event.target.closest && event.target.closest(PAYMENT_SELECTOR);
        if (!target) return;
        if (event.type === 'keydown') {
            const keys = ['Enter', ' ', 'Spacebar', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            if (keys.indexOf(event.key) === -1) return;
        }
        const wrapper = findWrapper(target);
        const cost = target.classList.contains('js-nao-method')
            ? parseInt(target.dataset.value, 10) || 0
            : currentShippingCost(wrapper);
        const params = baseParams(wrapper, cost);
        params.payment_method = methodLabel(target);
        fireOnce('AddPaymentInfo', 'AddPaymentInfo', params);
    });

    document.addEventListener('click', onPaymentInteraction);
    document.addEventListener('keydown', onPaymentInteraction);

    // Lead — el usuario dejó un dato de contacto (teléfono/WhatsApp/email)
    // aunque no llegue a confirmar la compra. Mejora además el match rate
    // (Advanced Matching) de todos los eventos siguientes.
    document.addEventListener('focusout', safe(function (event) {
        const field = event.target.closest && event.target.closest(
            '.js-nao-form input[name="phone"], .js-nao-form input[name="whatsapp_phone"], .js-nao-form input[name="email"]'
        );
        if (!field || !field.value) return;

        const isEmail = field.name === 'email';
        const digits = field.value.replace(/\D/g, '');
        const looksValid = isEmail
            ? /.+@.+\..+/.test(field.value)
            : digits.length >= 10;
        if (!looksValid) return;

        const wrapper = findWrapper(field);
        applyAdvancedMatching(wrapper);

        const params = baseParams(wrapper, currentShippingCost(wrapper));
        fireOnce('Lead', 'Lead', params);
    }));

    // Purchase — SOLO contraentrega: nao-checkout.js despacha `nao:purchase`
    // cuando el backend confirma la orden creada. El eventID se deriva del
    // número de orden para que sea estable (dedup con cualquier evento
    // server-side futuro que use la misma convención).
    document.addEventListener('nao:purchase', safe(function (event) {

        const detail = event.detail || {};
        const orderKey = String(detail.orderNumber || '').replace(/[^\w]/g, '');
        const flag = 'purchase_' + (orderKey || 'unknown');
        if (orderKey && hasFlag(flag)) return;
        markFlag(flag);

        const wrapper = findWrapper(event.target);
        applyAdvancedMatching(wrapper);

        const params = baseParams(wrapper, detail.shippingCost || 0);
        if (detail.total) params.value = detail.total;
        params.payment_method = detail.paymentMethod || '';
        params.num_items = detail.quantity || currentQuantity(wrapper) || 1;

        const id = orderKey ? 'nao_purchase_' + orderKey : eventId('Purchase');
        track('Purchase', params, id);
    }));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safe(initTracking));
} else {
    safe(initTracking)();
}
